import { createSelector } from 'reselect';
import { FrameRange, Interval } from 'content-creation-common/models';

import {
  isArray,
  isEmpty as isEmptyObject,
  isEmptyString,
  isNil,
  isNotNil,
  isObject,
} from '../../../util/object';
import {
  initializeArray,
  isEmpty as isEmptyArray,
  isNotEmpty,
  sortBy,
  sortByProperties,
} from '../../../util/array';
import {
  getAllElements,
  getAllElementsById,
  getById as getElementById,
  getElementDescendants,
  getElementDescendantsHelper,
  getLayerById,
  isAnyDescendantTransformedByUser,
} from '../../../selectors/element';
import {
  getDiscoverPublisher,
  getIsPublisherHappeningNow,
} from '../../discover/state-management/selectors';
import {
  getCurrentCreativeId,
  getRootEndFrame,
  getRootFrameRange,
  getTotalTime,
} from '../../shared/playback/state-management/selectors';
import Layer from '../../../model/Layer';
import Shot from '../../../model/Shot';
import Chapter from '../../../model/shot/Chapter';
import ChapterBreak from '../../../model/shot/ChapterBreak';
import ShotGroupMeta from '../../../model/shot/ShotGroupMeta';

import {
  CHAPTER_LENGTH_ABSOLUTE_MIN_SECOND,
  CHAPTER_LENGTH_MAX_SECOND,
  CHAPTER_LENGTH_MAX_SECOND_FOR_PUBLISHER,
  CHAPTER_LENGTH_MAX_SECOND_FOR_SHOW_PUBLISHER,
  CHAPTER_LENGTH_MIN_SECOND,
  LEFT_PANEL_TABS,
  MAXIMUM_LETTERBOX_THRESHOLD,
  MEDIA_SEGMENT_EDIT_LAYOUT_SUPPORTS_DIRECT_MANIUPULATION,
  MIN_SOURCE_MEDIA_RATIO_FOR_LAYOUTS,
  SHOT_BACKGROUND_TYPES,
  SHOT_ELEMENT_TYPES,
  SHOT_LAYOUT_SIZES,
  SHOT_LIST_THUMBNAIL_SIZE,
  SHOT_VIEW_HEIGHT_FIT_WIDTH,
} from '../constants';
import {
  durationSet,
  getAllMediaById,
  isUploadComplete,
} from '../../../selectors/media';
import { getAllContentById } from '../../../selectors/content';
import {
  getAllCreativeLayersById,
  getStateCreative,
  getCreativeRectangle,
  getSortedOutputRanges,
} from '../../creative/state-management/selectors';
import Frame from '../../../model/Frame';
import {
  generateTransformInfoFromLayers,
  getBoundsByShotViewElementIdHelper,
  getConstraintsByShotViewElementIdHelper,
  getFrameRangesNotRepresentedByLayers,
  getLetterBoxLayoutHeights,
  getSplitVertical2LayoutHeights,
  getSplitVertical3LayoutHeights,
  getStaticCropLayoutHeights,
} from './utils';
import { getEnvironmentSafeVRThumbnailUrl } from '../util/media';
import {
  containsInclusive,
  isNotNumber,
  isNumber,
  roundToDecimal,
} from '../../../util/numbers';
import { MEDIA_SEGMENT_EDIT_TYPE } from '../../../model/media/constants';
import { DEFAULT_KEYFRAME_NUMBER } from '../../creative/constants';
import {
  PLAYBACK_FRAMES_PER_SECOND,
  SECOND_IN_MILLISECONDS,
} from '../../../constants';
import { createKeyedStateSelector } from '../../shared/utils/selectorUtils';
import { getIsShowingReviewModal } from '../../review/state-management/selectors';
import { inEpisodeFlow as isInEpisodeFlow } from '../../routes/state-management/selectors';
import { getCreativeId, isUserAnAdvertiser } from '../../../selectors/ui';
import {
  getEpisodeMinChapterLengthInSecondsFromFeatureFlag,
  getIsChapterBreaksEnabled,
  getIsChapterBreaksShownByDefaultEnabled,
  getTargetChapterLengthFromFeatureFlag,
} from '../../feature-flags/state-management/selectors';
import { getSelectedDisplayElementId } from '../../editor/shared/state-management/selectors';
import { getMaxEpisodeDurationInSeconds } from '../../shared/creative/state-management/selectors';
import ShotBackground from '../../../model/shot/ShotBackground';
import ShotBackgroundCollection from '../../../model/shot/ShotBackgroundCollection';
import { getShotsForFrameRange } from '../util/chapterBreaks';
import { getCurrentFrame } from '../../playback/manager/PlaybackManager';
import { EPISODE_TIMELINE_ZOOMED_IN_WIDTH_IN_FRAMES } from '../../shared/timeline/constants';
import { EPISODE_MAX_LENGTH_WIGGLE_ROOM_IN_SECONDS } from '../../shared/creative/state-management/constants';

/** TODO: Update and fix as needed
 * @function getSelectedShotSuggestedEdits
 * @param  {Object} state
 * @return {Object<MEDIA_SEGMENT_EDIT_TYPE, MediaSegmentEdit>}
 */
// export const getSelectedShotSuggestedEdits = (state) => {
//   const selectedShot = getSelectedShot(state);
//   if (Shot.isShot(selectedShot)) {
//     return selectedShot.suggestedEdits;
//   }
//   return null;
// };

/** TODO: Update and fix as needed
 * @function getSelectedShotEditType TODO(svij) Use data from creative to augment the return type
 * in this object to account for currently active transformations. i.e. if user has pre-augmented
 * transformations, use them here instead.
 * @param  {MEDIA_SEGMENT_EDIT_TYPE} editType
 * @return {MediaSegmentEdit}
 */
// export const getSelectedShotEditByType = (state, editType) => {
//   const selectedShotEdits = getSelectedShotSuggestedEdits(state);
//   return isEmpty(selectedShotEdits) ? null : selectedShotEdits[editType];
// };

export const areAllShotsDetected = (state) =>
  state.videoReformatting.allShotsDetected;
export const getIsCreatingEpisode = (state) =>
  state.videoReformatting.isCreatingEpisode;
export const getIsEpisodeTimelineScrolling = (state) =>
  state.videoReformatting.isEpisodeTimelineScrolling;
export const getIsLoadingBreaks = (state) =>
  state.videoReformatting.isLoadingBreaks;
export const getIsShotBreaksShown = (state) =>
  state.videoReformatting.isShotBreaksShown;
export const getIsZoomedIn = (state) => state.videoReformatting.isZoomedIn;
export const getSelectedShotRanges = (state) =>
  state.videoReformatting.selectedShotRanges;
export const getShotsByFrameRange = (state) =>
  state.videoReformatting.shotsByFrameRange;
export const getShotFrameRangeIds = (state) => state.videoReformatting.shots;
export const getShotListComplete = (state) =>
  state.videoReformatting.shotListComplete;
export const getActiveShotElementId = (state) =>
  state.videoReformatting.activeShotElementId;
export const getActiveLeftPanelTab = (state) =>
  state.videoReformatting.activeLeftPanelTab;

export const getAnalyzedShotCount = createSelector(
  [getShotFrameRangeIds, getShotsByFrameRange],
  (allShotFrameRangeIds, shotMap) => {
    if (isEmptyObject(allShotFrameRangeIds)) {
      return 0;
    }

    return allShotFrameRangeIds.reduce((total, shotFrameRangeId) => {
      const shot = shotMap[shotFrameRangeId];
      if (Shot.isShot(shot) && shot.isAnalysisComplete) {
        total++;
      }

      return total;
    }, 0);
  },
);

// todo (chuti) use just CreativeLayer instead of both CreativeLayer and Layer models
export const getAllLayersById = createSelector(
  [getAllCreativeLayersById],
  (allLayersById) => {
    const layers = new Map();
    allLayersById.forEach((creativeLayer) => {
      layers.set(
        creativeLayer.elementId,
        Layer.getLayerFromCreativeDocumentObjects(
          creativeLayer.content,
          creativeLayer.element,
          creativeLayer.media,
        ),
      );
    });

    return layers;
  },
);

export const getShotCount = createSelector(
  [getShotFrameRangeIds],
  (allShotFrameRangeIds) => {
    return isEmptyObject(allShotFrameRangeIds)
      ? 0
      : allShotFrameRangeIds.length;
  },
);

export const getShotByFrameRange = createKeyedStateSelector(
  [getShotsByFrameRange, (state, frameRange) => frameRange],
  (shotsByFrameRange, frameRange) => {
    if (
      !FrameRange.isFrameRange(frameRange) ||
      isEmptyString(frameRange.toString())
    ) {
      return null;
    }

    return shotsByFrameRange[frameRange.toString()];
  },
);

/**
 * @function getAllShotContainers
 * @param {Object} state
 * @return {Object[]} Elements that correspond to a shotGroup
 */
export const getAllShotContainers = createSelector(
  [getAllElements],
  (elements) => {
    return elements.filter(
      ({ meta }) =>
        isNotNil(meta) && meta.shotType === SHOT_ELEMENT_TYPES.GROUP,
    );
  },
);

export const getActiveShotContainer = createSelector(
  [getActiveShotElementId, getAllShotContainers],
  (activeShotElementId, allShotContainers) => {
    return allShotContainers.find(
      (element) => element.elementId === activeShotElementId,
    );
  },
);

export const getActiveShotLayer = createSelector(
  [getActiveShotElementId, getAllLayersById],
  (activeShotElementId, allLayersById) =>
    allLayersById.get(activeShotElementId),
);

/**
 * @function getAllShotContainersSortedByStartFrame
 * @return {Object[]} Elements that correspond to a shotGroup - sorted by their start frames
 */
export const getAllShotContainersSortedByStartFrame = createSelector(
  [getAllShotContainers],
  (shotContainers) => {
    return sortBy(shotContainers, 'frameStart');
  },
);

/**
 * @function getEndFrameOfLastShotContainer
 * @return {Number} End frame number of last shot container
 */
export const getEndFrameOfLastShotContainer = createSelector(
  [getAllShotContainersSortedByStartFrame],
  (allShotContainers) => {
    const numShotContainers = allShotContainers.length;
    const lastShotContainer = allShotContainers[numShotContainers - 1];
    return isNotNil(lastShotContainer) && isNumber(lastShotContainer.frameEnd)
      ? lastShotContainer.frameEnd
      : -1;
  },
);

/**
 * @function getAllShotContainerLayers
 * @param {Object} state
 * @return {Layer[]} Layers that correspond to a shotGroup, sorted by start frame
 */
export const getAllShotContainerLayers = createSelector(
  [getAllShotContainersSortedByStartFrame, getAllLayersById],
  (allShotContainers, allLayersById) => {
    if (allLayersById.size === 0) return [];
    return allShotContainers.map((shotContainer) =>
      allLayersById.get(shotContainer.elementId),
    );
  },
);

export const getNonSkippedShotContainerLayers = createSelector(
  [getAllShotContainerLayers],
  (allShotContainerLayers) => {
    return allShotContainerLayers.filter(
      (shotContainerLayer) => shotContainerLayer.meta.shotSkipped !== true,
    );
  },
);

export const getSkippedShotContainerLayers = createSelector(
  [getAllShotContainerLayers],
  (allShotContainerLayers) => {
    return allShotContainerLayers.filter(
      (shotContainerLayer) => shotContainerLayer.meta.shotSkipped === true,
    );
  },
);

/**
 * @function getShotGroupElementForFrame
 * @return {Object} A Shot Container Element that spans the provided frame number
 */
export const getShotGroupElementForFrame = createKeyedStateSelector(
  [getAllShotContainers, (state, frameNumber) => frameNumber],
  (shotContainers, frameNumber) => {
    return shotContainers.find(({ frameStart, frameEnd }) =>
      containsInclusive(frameNumber, frameStart, frameEnd),
    );
  },
);

/**
 * @function get
 * @return {Object} A Shot Container Element that spans the provided frame number
 */
export const getAllShotGroupLayersContainingFrameRange = createKeyedStateSelector(
  [getAllShotContainerLayers, (state, frameRange) => frameRange],
  (shotContainers, frameRange) => {
    if (isEmptyArray(shotContainers) || !FrameRange.isFrameRange(frameRange)) {
      return [];
    }
    const { end, start } = frameRange;
    return shotContainers.filter(
      ({ startFrame, endFrame }) => startFrame >= start && endFrame <= end,
    );
  },
);

/**
 * @function getShotContainingFrameHelper
 * @param {Object[]} shotContainers
 * @param {Number} frameNumber
 * @return {Shot} the shot object containing the given frame
 */
export const getShotContainingFrameHelper = (shotContainers, frameNumber) =>
  shotContainers.find(({ frameStart, frameEnd }) =>
    containsInclusive(frameNumber, frameStart, frameEnd),
  );

export const getActiveShotContainerForFrame = createSelector(
  [getAllShotContainers, (state, frameNumber) => frameNumber],
  (shotContainers, frameNumber) => {
    return getShotContainingFrameHelper(shotContainers, frameNumber);
  },
);

export const getActiveShotContainerForCurrentFrame = createSelector(
  [getAllShotContainers, getCurrentFrame],
  (shotContainers, frameNumber) => {
    return getShotContainingFrameHelper(shotContainers, frameNumber);
  },
);

export const getIsActiveShotContainerSkipped = createSelector(
  [getActiveShotContainerForCurrentFrame],
  (activeShotContainer) =>
    isNotNil(activeShotContainer) &&
    isNotNil(activeShotContainer.meta) &&
    Boolean(activeShotContainer.meta.shotSkipped),
);

export const getActiveShotGroupLayerForCurrentFrame = createSelector(
  [getActiveShotContainer, getAllLayersById],
  (activeShotContainer, allLayersById) => {
    if (isNil(activeShotContainer)) return undefined;
    const { elementId } = activeShotContainer;
    return allLayersById.get(elementId);
  },
);

export const getSortedSelectedShotRanges = createSelector(
  [getSelectedShotRanges, getActiveShotContainer],
  (selectedShotRanges, activeShot) => {
    if (isNil(selectedShotRanges)) {
      if (isNil(activeShot)) return [];
      const { frameStart, frameEnd } = activeShot;
      if (isNil(frameStart) || isNil(frameEnd)) return [];
      const activeRange = new FrameRange(frameStart, frameEnd);
      return [activeRange];
    }
    return sortByProperties(selectedShotRanges, [{ key: 'start' }]);
  },
);

export const getFirstSelectedShotRanges = createSelector(
  [getSortedSelectedShotRanges],
  (currentRanges) => {
    if (!isArray(currentRanges) || currentRanges.length !== 1) return null;
    return currentRanges[0];
  },
);

export const getSelectedShotContainerLayers = createSelector(
  [getSortedSelectedShotRanges, getAllShotContainerLayers, getActiveShotLayer],
  (sortedSelectedShotRanges, allShotContainerLayers, activeShotLayer) => {
    if (isEmptyArray(allShotContainerLayers)) return [];
    if (isEmptyArray(sortedSelectedShotRanges)) {
      return Layer.isLayer(activeShotLayer) ? [activeShotLayer] : [];
    }

    const shotContainers = [];
    let currentShotContainerIndex = 0;
    // since both shot ranges and shot containers are sorted by startFrame
    // we can keep track of which shot container we are checking to avoid O(n*m)
    sortedSelectedShotRanges.forEach((sortedSelectedShotRange) => {
      for (
        let i = currentShotContainerIndex;
        i < allShotContainerLayers.length;
        i++
      ) {
        const currentShotContainerLayer = allShotContainerLayers[i];
        const { endFrame, startFrame } = currentShotContainerLayer;

        // if sorted selected shot range does not contain the current shot container's frame range we check the following:
        // 1) if this is the first shot of the current selected range, if not,
        // 2) we're done with the current selected range and we can move onto the next selected range
        if (
          !sortedSelectedShotRange.contains(
            new FrameRange(startFrame, endFrame),
          )
        ) {
          if (currentShotContainerIndex === i) {
            currentShotContainerIndex++;
            continue;
          } else {
            currentShotContainerIndex = i;
            break;
          }
        }

        shotContainers.push(currentShotContainerLayer);
      }
    });

    return shotContainers;
  },
);

export const getActiveShotFrameRange = createSelector(
  [getActiveShotContainer],
  (activeShot) => {
    return isNotNil(activeShot)
      ? new FrameRange(activeShot.frameStart, activeShot.frameEnd)
      : undefined;
  },
);

export const getActiveShotLayoutTypeForCurrentFrame = createSelector(
  [getActiveShotContainer],
  (activeShotContainer) => {
    if (
      isNil(activeShotContainer) ||
      isNil(activeShotContainer.meta) ||
      isNil(activeShotContainer.meta.layoutType)
    ) {
      return MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP;
    }
    return activeShotContainer.meta.layoutType;
  },
);

/**
 * Get a list of shot layers around the active shot that are skipped.
 * If the active shot is not-skipped, this should return no layers.
 * @returns {Layer[]} a list of shot group layers.
 */
export const getAdjacentSkippedShotContainerLayers = createSelector(
  [
    isInEpisodeFlow,
    getActiveShotGroupLayerForCurrentFrame,
    getSkippedShotContainerLayers,
  ],
  (inEpisodeFlow, activeShotContainer, skippedShotContainerLayers) => {
    if (!inEpisodeFlow) return [];
    if (
      isNil(activeShotContainer) ||
      isNil(activeShotContainer.meta) ||
      activeShotContainer.meta.shotSkipped !== true
    ) {
      return [];
    }
    const { startFrame: activeStartFrame } = activeShotContainer;

    let consecutiveShots = [];
    let inAdjacentShotRange = false;
    for (const skippedShot of skippedShotContainerLayers) {
      const { startFrame } = skippedShot;
      if (isEmptyObject(consecutiveShots)) {
        consecutiveShots.push(skippedShot);
      } else {
        const lastConsecutiveShot =
          consecutiveShots[consecutiveShots.length - 1];
        // if next skipped shot is adjacent, include it
        if (startFrame === lastConsecutiveShot.endFrame + 1) {
          consecutiveShots.push(skippedShot);
        } else {
          // there's a gap in the skipped shots
          if (inAdjacentShotRange) {
            break;
          } else {
            consecutiveShots = [skippedShot];
          }
        }
      }

      if (startFrame === activeStartFrame) {
        inAdjacentShotRange = true;
      }
    }
    return consecutiveShots;
  },
);

export const getAdjacentSkippedShotFrameRange = createSelector(
  [getAdjacentSkippedShotContainerLayers],
  (adjacentSkippedShotContainerLayers) => {
    const frameRanges = FrameRange.union(
      adjacentSkippedShotContainerLayers.map(
        ({ endFrame, startFrame }) => new FrameRange(startFrame, endFrame),
      ),
    );

    if (isEmptyArray(frameRanges)) return null;
    return frameRanges[0];
  },
);

/**
 * @function getShotNumberForFrame
 * @param {Object[]} sortedShotContainers
 * @param {Number} frameNumber
 * @return {Number} the index number of the shot containing the given frame (indexed from 1)
 */
const getShotNumberForFrame = (sortedShotContainers, frameNumber) => {
  if (isEmptyArray(sortedShotContainers)) return null;
  let shotIndex = 0;
  sortedShotContainers.some((shotContainer, index) => {
    const { frameStart, frameEnd } = shotContainer;
    if (containsInclusive(frameNumber, frameStart, frameEnd)) {
      shotIndex = index;
      return true;
    }
    return false;
  });
  return shotIndex + 1;
};

const selectedShotNumberIntervalHelper = (
  shotContainers,
  selectedShotRange,
) => {
  let startingShot = getShotNumberForFrame(
    shotContainers,
    selectedShotRange.start,
  );
  let endingShot = getShotNumberForFrame(shotContainers, selectedShotRange.end);
  if (isNil(startingShot)) {
    startingShot = 1;
  }
  if (isNil(endingShot)) {
    startingShot = 1;
    endingShot = 1;
  }
  return new Interval(startingShot, endingShot);
};

/**
 * @function getSelectedShotNumberIntervals
 * @param {Object} state
 * @return {array} of intervals of starting shot number to ending shot number
 */
export const getSelectedShotNumberIntervals = createSelector(
  [
    getAllShotContainersSortedByStartFrame,
    getActiveShotContainer,
    getSortedSelectedShotRanges,
  ],
  (shotContainers, activeShotContainer, sortedSelectedShotRanges) => {
    if (isEmptyArray(sortedSelectedShotRanges)) {
      if (isNil(activeShotContainer)) return [new Interval(1, 1)];
      const { frameStart, frameEnd } = activeShotContainer;
      const activeShotFrameRange = new FrameRange(frameStart, frameEnd);
      return [
        selectedShotNumberIntervalHelper(shotContainers, activeShotFrameRange),
      ];
    }
    const intervals = [];
    sortedSelectedShotRanges.forEach((selectedShotRange) => {
      intervals.push(
        selectedShotNumberIntervalHelper(shotContainers, selectedShotRange),
      );
    });
    return intervals;
  },
);

const getShotLayoutTypesOfSelectedShotsHelper = (
  sortedShotContainers,
  selectedShotRange,
) => {
  const layouts = new Set();

  sortedShotContainers.forEach((shotContainer) => {
    if (isNil(shotContainer)) return;
    if (
      selectedShotRange.contains(
        new FrameRange(shotContainer.frameStart, shotContainer.frameEnd),
      )
    ) {
      if (isNil(shotContainer.meta) || isNil(shotContainer.meta.layoutType)) {
        layouts.add(MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP);
      } else {
        layouts.add(shotContainer.meta.layoutType);
      }
    }
  });
  return layouts;
};

/**
 * @function getShotLayoutTypesOfSelectedShots
 * @param {Object} state
 * @return {Set} of layouts of selected shots (or of the first shot if there's no selection)
 */
export const getShotLayoutTypesOfSelectedShots = createSelector(
  [
    getAllShotContainersSortedByStartFrame,
    getActiveShotContainer,
    getSortedSelectedShotRanges,
  ],
  (sortedShotContainers, activeShotContainer, sortedSelectedShotRanges) => {
    if (isEmptyArray(sortedSelectedShotRanges)) {
      if (isNil(activeShotContainer)) return new Set();

      const { frameEnd, frameStart } = activeShotContainer;
      const activeFrameRange = new FrameRange(frameEnd, frameStart);
      return getShotLayoutTypesOfSelectedShotsHelper(
        sortedShotContainers,
        activeFrameRange,
      );
    }

    const layoutsSet = new Set();
    sortedSelectedShotRanges.forEach((selectedShotRange) => {
      const layouts = getShotLayoutTypesOfSelectedShotsHelper(
        sortedShotContainers,
        selectedShotRange,
      );
      layouts.forEach((layout) => layoutsSet.add(layout));
    });
    return layoutsSet;
  },
);

/**
 * @function getSourceShotElement
 * @return {object} source shot element (element with meta.shotType 'SOURCE')
 */
export const getSourceShotElement = createSelector(
  [getAllElements],
  (allElements) => {
    return allElements.find(
      ({ meta }) =>
        isNotNil(meta) && meta.shotType === SHOT_ELEMENT_TYPES.SOURCE,
    );
  },
);

/**
 * @function getAllSourceShotElements
 * @return {object} elements with with meta.shotType 'SOURCE'
 */
export const getAllSourceShotElements = createSelector(
  [getAllElements],
  (allElements) => {
    return allElements.filter(
      ({ meta }) =>
        isNotNil(meta) && meta.shotType === SHOT_ELEMENT_TYPES.SOURCE,
    );
  },
);

/**
 * @function getAllSourceShotElementsSortedByStartFrame
 * @return {object} elements with with meta.shotType 'SOURCE' sorted by frame start
 */
export const getAllSourceShotElementsSortedByStartFrame = createSelector(
  [getAllSourceShotElements],
  (sourceElements) => {
    return sortBy(sourceElements, 'frameStart');
  },
);

/**
 * @function getAllSourceLayers
 * @return {object} elements with with meta.shotType 'SOURCE' sorted by frame start
 */
export const getAllSourceLayers = createSelector(
  [getAllSourceShotElementsSortedByStartFrame, getAllLayersById],
  (sourceElements, allLayers) => {
    return sourceElements.map((element) => allLayers.get(element.elementId));
  },
);

/**
 * @function getNumberOfClips
 * @return {number}
 */
export const getNumberOfClips = createSelector(
  [getAllSourceShotElementsSortedByStartFrame],
  (sourceElements) => {
    return sourceElements.length;
  },
);

/**
 * @function getAllSourceMediaServerIds
 * @return {array} sourceVideoServerIds sorted by start frame
 */
export const getAllSourceMediaServerIds = createSelector(
  [getAllSourceLayers],
  (sourceLayers) => {
    return sourceLayers.map((layer) => layer.mediumServerId);
  },
);

/**
 * @function getLastSourceLayer
 * @return {object} the last (wrt time) source shot element (elements with meta.shotType 'SOURCE')
 */
export const getLastSourceLayer = createSelector(
  [getAllSourceShotElementsSortedByStartFrame, getAllLayersById],
  (sourceShotElements, allLayers) => {
    if (isEmptyArray(sourceShotElements)) return null;

    const element = sourceShotElements[sourceShotElements.length - 1];
    const { elementId } = element;
    return allLayers.get(elementId);
  },
);

/**
 * @function getSourceVideoCreativeLayersByFrameRange
 * @return Map of (sorted) Source Video Creative Layer keyed by frame ranges
 */
export const getSourceVideoCreativeLayersByFrameRange = createSelector(
  [getAllSourceShotElementsSortedByStartFrame, getAllCreativeLayersById],
  (allSourceShotElements, allCreativeLayersById) => {
    const sourceVideoCreativeLayersByFrameRange = new Map();
    allSourceShotElements.forEach((sourceShotElement) => {
      const { elementId } = sourceShotElement;
      const sourceVideoCreativeLayer = allCreativeLayersById.get(elementId);
      if (isNil(sourceVideoCreativeLayer)) return;

      sourceVideoCreativeLayersByFrameRange.set(
        sourceVideoCreativeLayer.frameRange,
        sourceVideoCreativeLayer,
      );
    });

    return sourceVideoCreativeLayersByFrameRange;
  },
);

/**
 * @function getSourceVideoCreativeLayersByFrameRange
 * @return Map of (sorted) Source Video Creative Layer keyed by frame ranges
 */
export const getClipFrameRanges = createSelector(
  [getAllSourceShotElementsSortedByStartFrame, getAllCreativeLayersById],
  (allSourceShotElements, allCreativeLayersById) => {
    const clipFrameRanges = new Set();
    allSourceShotElements.forEach((sourceShotElement) => {
      const { elementId } = sourceShotElement;
      const sourceVideoCreativeLayer = allCreativeLayersById.get(elementId);
      if (isNil(sourceVideoCreativeLayer)) return;

      clipFrameRanges.add(sourceVideoCreativeLayer.frameRange);
    });

    return clipFrameRanges;
  },
);

/**
 * @function getSourceVideoCreativeLayersByShotContainerElementId
 * @return Source Video Creative Layer indexed by container's elementId
 */
export const getSourceVideoCreativeLayersByShotContainerElementId = createKeyedStateSelector(
  [
    getAllShotContainersSortedByStartFrame,
    getSourceVideoCreativeLayersByFrameRange,
  ],
  (shotContainers, sourceVideoCreativeLayersByFrameRange) => {
    if (isEmptyArray(shotContainers)) return {};

    const sourceVideoCreativeLayersByShotContainerElementId = {};

    // since both shot containers and source layers are sorted,
    // we can compare them in order and avoid compare all containers against all sources
    let currentShotContainerIndex = 0;

    for (const [
      frameRange,
      sourceVideoCreativeLayer,
    ] of sourceVideoCreativeLayersByFrameRange.entries()) {
      // check against all containers up to the point where the frameStart doesn't exist in the current source's frame Range
      for (let i = currentShotContainerIndex; i < shotContainers.length; i++) {
        const { elementId, frameStart } = shotContainers[i];
        if (!frameRange.containsFrameNumber(frameStart)) {
          currentShotContainerIndex = i;
          break;
        }

        sourceVideoCreativeLayersByShotContainerElementId[
          elementId
        ] = sourceVideoCreativeLayer;
      }
    }

    return sourceVideoCreativeLayersByShotContainerElementId;
  },
);

/**
 * @function getShotContainersByClipIndex
 * @return array<array>
 */
export const getShotContainersOrderedByClip = createKeyedStateSelector(
  [
    getAllShotContainerLayers,
    getSourceVideoCreativeLayersByFrameRange,
    getClipFrameRanges,
  ],
  (
    shotContainerLayers,
    sourceVideoCreativeLayersByFrameRange,
    clipFrameRanges,
  ) => {
    if (isEmptyArray(shotContainerLayers)) return [];
    if (clipFrameRanges.size === 0) return [];

    const shotContainerLayersOrderedByClip = [];

    // since both shot containers and source layers are sorted,
    // we can compare them in order and avoid compare all containers against all sources
    let currentShotContainerLayerIndex = 0;
    for (const frameRange of clipFrameRanges) {
      // check against all containers up to the point where the frameStart doesn't exist in the current source's frame Range
      const currentClipShots = [];
      for (
        let i = currentShotContainerLayerIndex;
        i < shotContainerLayers.length;
        i++
      ) {
        const currentShotContainerLayer = shotContainerLayers[i];
        const { startFrame } = currentShotContainerLayer;
        if (!frameRange.containsFrameNumber(startFrame)) {
          currentShotContainerLayerIndex = i;
          break;
        }

        currentClipShots.push(currentShotContainerLayer);
      }

      shotContainerLayersOrderedByClip.push(currentClipShots);
    }

    return shotContainerLayersOrderedByClip;
  },
);

/**
 * @function getSourceShotElementForCurrentFrame
 * @return {object} the source shot element for the current frame
 */
export const getSourceShotElementForCurrentFrame = createSelector(
  [getAllSourceShotElements, getCurrentFrame],
  (sourceShotElements, currentFrame) => {
    return sourceShotElements.find(({ frameEnd, frameStart }) => {
      try {
        const frameRange = new FrameRange(frameStart, frameEnd);
        return frameRange.containsFrameNumber(currentFrame);
      } catch (e) {
        return false;
      }
    });
  },
);

/**
 * @function getSourceVideoCreativeLayer
 * @param  {Object} state
 * @return {CreativeLayer} Video Creative Layer
 */
export const getSourceVideoCreativeLayer = createSelector(
  [getStateCreative, getSourceShotElementForCurrentFrame],
  (creative, sourceShotElement) => {
    return isNotNil(sourceShotElement)
      ? creative.layersById.get(sourceShotElement.elementId)
      : null;
  },
);

/**
 * @function getCurrentSourceLayer
 * @param  {Object} state
 * @return {CreativeLayer} UI Layer
 */
export const getCurrentSourceLayer = createSelector(
  [getAllLayersById, getSourceShotElementForCurrentFrame],
  (allLayers, sourceShotElement) => {
    return isNotNil(sourceShotElement)
      ? allLayers.get(sourceShotElement.elementId)
      : null;
  },
);

/**
 * @function getCurrentSourceLayerFrameRange
 * @param  {Object} state
 * @return {FrameRange}
 */
export const getCurrentSourceLayerFrameRange = createSelector(
  [getCurrentSourceLayer],
  (sourceLayer) => {
    return Layer.isLayer(sourceLayer)
      ? new FrameRange(sourceLayer.startFrame, sourceLayer.endFrame)
      : null;
  },
);
/**
 * @function getShotGroupElementByFrameRange
 * @param  {FrameRange} frameRange
 * @return {object} element Object that corresponds to
 * the given frame range beginning at frame range's start and is a group shot element
 * element can be from frame 0-9, and requested frame range is 0-4 and be valid
 */
export const getShotGroupElementByFrameRange = createKeyedStateSelector(
  [getAllElements, (state, frameRange) => frameRange],
  (allElements, frameRange) => {
    if (!FrameRange.isFrameRange(frameRange)) {
      return null;
    }

    return allElements.find(({ frameStart, frameEnd, meta }) => {
      const elementFrameRange = new FrameRange(frameStart, frameEnd);
      return (
        isNotNil(meta) &&
        meta.shotType === SHOT_ELEMENT_TYPES.GROUP &&
        elementFrameRange.equals(frameRange)
      );
    });
  },
);

/**
 * @function getShotGroupElementIdByFrameRange
 * @param  {FrameRange} frameRange
 * @return {string} elementId corresponds to the given frame range and is a group shot element
 * element can be from frame 0-9, and requested frame range is 0-4 and be valid
 */
export const getShotGroupElementIdByFrameRange = createSelector(
  [getShotGroupElementByFrameRange],
  (shotGroupElement) => {
    if (isNil(shotGroupElement)) {
      return null;
    }
    return shotGroupElement.elementId;
  },
);

/**
 * @function isShotTransformedByUser
 * @param  {object} state
 * @param  {string} elementId of shot group
 * @return {bool}
 * element can be from frame 0-9, and requested frame range is 0-4 and be valid
 */
export const isShotTransformedByUser = createSelector(
  [getLayerById, isAnyDescendantTransformedByUser],
  (layer, descendantTransformedByUser) => {
    if (!Layer.isLayer(layer)) return false;
    return layer.isTransformedByUser || descendantTransformedByUser;
  },
);

/**
 * @function isShotAnalysisComplete
 * @param  {object} state
 * @param  {string} elementId of shot group
 * @return {bool}
 */
export const isShotAnalysisComplete = createSelector(
  [getLayerById, isShotTransformedByUser, getShotsByFrameRange],
  (layer, transformed, shotsByFrameRange) => {
    if (!Layer.isLayer(layer)) return false;

    const { endFrame, startFrame } = layer;
    const frameRange = new FrameRange(startFrame, endFrame);
    const shot = shotsByFrameRange[frameRange.toString()];
    const hasShot = Shot.isShot(shot);

    return !hasShot || transformed || shot.isAnalysisComplete;
  },
);

const filterDescendants = (allLayersById, elementDescendants, predicate) => {
  return elementDescendants.reduce((all, elementId) => {
    if (allLayersById.has(elementId)) {
      const layer = allLayersById.get(elementId);
      if (predicate(layer)) {
        all.push(layer);
      }
    }
    return all;
  }, []);
};

const getAllLayersOfTypeByShotGroupElementIdHelper = (
  allLayersById,
  elementDescendants,
  shotElementType,
) => {
  return filterDescendants(allLayersById, elementDescendants, (layer) => {
    return layer.meta.shotType === shotElementType;
  });
};

export const getAllLayersOfTypeByShotGroupElementId = createSelector(
  [
    getAllLayersById,
    getElementDescendants,
    (state, groupElementId, shotElementType) => shotElementType,
  ],
  (allLayersById, elementDescendants, shotElementType) =>
    getAllLayersOfTypeByShotGroupElementIdHelper(
      allLayersById,
      elementDescendants,
      shotElementType,
    ),
);

const getAllShotMaskLayersByShotGroupElementIdHelper = (
  allLayersById,
  elementDescendants,
) => {
  return filterDescendants(allLayersById, elementDescendants, (layer) => {
    return layer.meta.shotType === SHOT_ELEMENT_TYPES.MASK;
  });
};

const getAllShotMaskLayersByShotGroupElementId = createSelector(
  [getAllLayersById, getElementDescendants],
  (allLayersById, elementDescendants) =>
    getAllShotMaskLayersByShotGroupElementIdHelper(
      allLayersById,
      elementDescendants,
    ),
);

const getBackgroundLayerByShotGroupElementIdHelper = (
  allLayersById,
  elementDescendants,
) => {
  const backgroundLayers = filterDescendants(
    allLayersById,
    elementDescendants,
    (layer) => {
      return layer.meta.shotType === SHOT_ELEMENT_TYPES.BACKGROUND;
    },
  );
  if (isEmptyArray(backgroundLayers)) return null;
  return backgroundLayers[0];
};

export const getBackgroundLayerByShotGroupElementId = createKeyedStateSelector(
  [getAllLayersById, getElementDescendants],
  (allLayersById, elementDescendants) =>
    getBackgroundLayerByShotGroupElementIdHelper(
      allLayersById,
      elementDescendants,
    ),
);

/**
 * @function getShotViewElementByShotGroupElementId
 * @param  {string} elementId of group shot
 * @param  {string} shotViewIndex view index of group shot element's children
 * @return {object} element that corresponds to the given elementId and shotViewIndex
 */
export const getShotViewElementByShotGroupElementId = createSelector(
  [
    getAllElementsById,
    getLayerById,
    (state, elementId, shotViewIndex) => shotViewIndex,
  ],
  (elementMap, layer, shotViewIndex = 0) => {
    if (!Layer.isLayer(layer) || isNil(layer.content)) return null;

    const { content } = layer;
    const { contentElementIds } = content;
    return elementMap[contentElementIds[shotViewIndex]];
  },
);

const getShotMaskHeightsByShotGroupElementIdHelper = (
  allLayersById,
  shotMaskLayers,
) => {
  const maskHeights = shotMaskLayers.map((shotMaskLayer) => {
    const maskLayer = allLayersById.get(shotMaskLayer.elementId);
    const { transforms } = maskLayer;
    const currentTransform = transforms.get(DEFAULT_KEYFRAME_NUMBER);
    // All masks are set to width of the canvas, use height to determine scale.
    const {
      scale: { y: scale },
    } = currentTransform;
    return scale * maskLayer.height;
  });
  return maskHeights;
};

export const getShotMaskHeightsByShotGroupElementId = createSelector(
  [getAllLayersById, getAllShotMaskLayersByShotGroupElementId],
  (allLayersById, shotMaskLayers) =>
    getShotMaskHeightsByShotGroupElementIdHelper(allLayersById, shotMaskLayers),
);

/**
 * @function getSelectedOrActiveFrameRange
 * returns frame range for active or selected range
 */
export const getSelectedOrActiveFrameRange = createSelector(
  [getActiveShotFrameRange, getSortedSelectedShotRanges],
  (activeShotFrameRange, sortedSelectedShotRanges) => {
    if (isEmptyArray(sortedSelectedShotRanges)) return activeShotFrameRange;
    return sortedSelectedShotRanges[0];
  },
);

export const areAllSelectedShotsSkipped = createSelector(
  [getSelectedShotContainerLayers],
  (selectedShots) => {
    return selectedShots.every(
      (shotGroup) => shotGroup.meta.shotSkipped === true,
    );
  },
);

/**
 * @function getNumberOfSelectedShots
 * @param {Object} state
 * @return {number}
 */
export const getNumberOfSelectedShots = createSelector(
  [getSelectedShotContainerLayers],
  (selectedShots) => {
    return isEmptyArray(selectedShots) ? 0 : selectedShots.length;
  },
);

/**
 * @function getSelectedFrameRangeForTimeline
 * returns frame range for video reformatting timeline
 */
export const getSelectedFrameRangeForTimeline = createSelector(
  [getSelectedOrActiveFrameRange, getRootFrameRange],
  (selectedRange, rootFrameRange) => {
    if (FrameRange.isFrameRange(selectedRange)) {
      return selectedRange;
    }

    return rootFrameRange;
  },
);

/**
 * @function getLastSourceVideoServerId
 * @param  {Object} state
 * @return {id} server media id that corresponds to the last source video that was added
 */
export const getLastSourceVideoServerId = createSelector(
  [getAllSourceMediaServerIds],
  (sourceMediaServerIds) => {
    if (isEmptyArray(sourceMediaServerIds)) return null;
    return sourceMediaServerIds[sourceMediaServerIds.length - 1];
  },
);

/**
 * @function sourceVideoDurationSet
 * @param  {Object} media
 * @param  {Object} state
 * @return {Boolean} Whether or not the given media has ability to augment total frames in VR
 */
export const doesMediaAugmentTotalFrames = (media, state) => {
  return (
    (isNotNil(media.serverId) &&
      media.serverId !== getLastSourceVideoServerId(state)) ||
    (isUploadComplete(media) && durationSet(media))
  );
};

/**
 * @function getUnrepresentedFrameRanges
 * returns sub-frame ranges of the passed in frame range,
 * where there are no shots to represent those frames.
 * @param {Object} state
 * @param  {number} frameStart
 * @param  {number} frameEnd
 * @return  {FrameRange[]}
 */
export const getUnrepresentedFrameRanges = createKeyedStateSelector(
  [getAllShotContainerLayers, (state, frameRange) => frameRange],
  (shotGroupLayers, frameRange) => {
    return getFrameRangesNotRepresentedByLayers(shotGroupLayers, frameRange);
  },
);

/**
 * @function foundAllShotGroups
 * returns true/false whether shot group elements can satisfy the entire creative
 * @param {Object} state
 * @return  {bool}
 */
export const foundAllShotGroupsForEpisode = createSelector(
  [getAllShotContainers, getRootFrameRange],
  (shotGroups, creativeFrameRange) => {
    // validate if all shots are in range
    if (isEmptyArray(shotGroups)) return false;

    const unrepresentedFrameRanges = creativeFrameRange.relativeComplementIn(
      shotGroups.map(
        (group) => new FrameRange(group.frameStart, group.frameEnd),
      ),
    );

    return isEmptyObject(unrepresentedFrameRanges);
  },
);

// This is a selector created to help with all shots detection returning false from the server
export const areAllShotsDetectedOrCreated = createSelector(
  [areAllShotsDetected, foundAllShotGroupsForEpisode],
  (allShotsDetected, shotGroupsReady) => allShotsDetected || shotGroupsReady,
);

/**
 * @function canSplitShot
 * returns true/false whether you can split at the current frame
 * shot can only be split if it has more than one frame
 * and current frame is not on the first frame of the shot
 * @param {Object} state
 * @return  {bool}
 */
export const canSplitShot = createSelector(
  [getCurrentFrame, getActiveShotContainer],
  (currentFrame, activeShot) => {
    if (isNil(activeShot)) return false;
    const { frameStart, frameEnd } = activeShot;
    // don't really need frameEnd - frameStart check
    return currentFrame !== frameStart && frameEnd - frameStart > 0;
  },
);

export const getActiveShotFrameBoundaries = createSelector(
  [getActiveShotContainer],
  (activeShot) => {
    if (isNil(activeShot)) return null;
    const { frameStart, frameEnd } = activeShot;
    return new FrameRange(frameStart, frameEnd);
  },
);

export const getAreShotsLoaded = createSelector(
  [getAllSourceMediaServerIds, getAllShotContainers],
  (sourceMediaIds, shotContainers) => {
    return isNotEmpty(sourceMediaIds) && isNotEmpty(shotContainers);
  },
);

/**
 * @function getShotsByOutputRange
 * @param {Object} state
 * @return {Object} with keys like '[start, end]' and values consisting of arrays of shots
 */
export const getShotsByOutputRange = createSelector(
  [getSortedOutputRanges, getAllShotContainerLayers],
  (sortedOutputRanges, sortedShots) => {
    if (isEmptyArray(sortedOutputRanges)) return {};

    // Associate shots with the output ranges that they are in. We increment an index in sortedOutputRanges
    // until we find an output range that contains the current shot. Each shot and output range is visited
    // at most once.
    const outputRangesToShots = {};
    let outputRangesIdx = 0;
    sortedShots.forEach((shot) => {
      // Get FrameRange for this shot
      const { endFrame, startFrame } = shot;
      if (isNil(endFrame) || isNil(startFrame)) return;
      const shotFrameRange = new FrameRange(startFrame, endFrame);

      // Get FrameRange of containing output range
      let currentOutputFrameRange;
      for (
        ;
        outputRangesIdx < sortedOutputRanges.length;
        outputRangesIdx += 1
      ) {
        const currentOutputRange = sortedOutputRanges[outputRangesIdx];
        if (isNotNil(currentOutputRange)) {
          const { end, start } = currentOutputRange;
          if (isNotNil(end) && isNotNil(start)) {
            const currentFrameRange = new FrameRange(start, end);
            if (currentFrameRange.contains(shotFrameRange)) {
              currentOutputFrameRange = currentFrameRange;
              break;
            }
          }
        }
      }

      // If we found a containing output range, add it to the map from output ranges to shots
      if (isNotNil(currentOutputFrameRange)) {
        const outputRangeKey = currentOutputFrameRange.toString();
        // Initialize if we haven't seen this output range before
        if (!outputRangesToShots.hasOwnProperty(outputRangeKey)) {
          outputRangesToShots[outputRangeKey] = [];
        }
        outputRangesToShots[outputRangeKey].push(shot);
      }
    });

    return outputRangesToShots;
  },
);

/**
 * @function getChapters
 * @param {Object} state
 * @return {array} of each Chapter in this episode
 */
export const getChapters = createSelector(
  [getSortedOutputRanges, getShotsByOutputRange],
  (sortedOutputRanges, shotsByOutputRange) => {
    if (isEmptyArray(sortedOutputRanges)) return [];
    return sortedOutputRanges.map((outputRange, index) => {
      if (isNil(outputRange)) return null;
      const { end, start } = outputRange;
      if (isNil(end) || isNil(start)) return null;
      const outputRangeKey = new FrameRange(start, end).toString();
      let shots = [];
      if (shotsByOutputRange.hasOwnProperty(outputRangeKey)) {
        shots = shotsByOutputRange[outputRangeKey];
      }
      return new Chapter(index, start, end, shots);
    });
  },
);

export const getChapterBreaksByFrameStart = createSelector(
  [getChapters],
  (chapters) => {
    if (isEmptyArray(chapters)) return {};

    return chapters.reduce((chapterBreaksByFrameStart, chapter, index) => {
      const { frameStart } = chapter;
      const previousChapter = chapters[index - 1];
      chapterBreaksByFrameStart[frameStart] = new ChapterBreak(
        frameStart,
        previousChapter,
        chapter,
      );

      return chapterBreaksByFrameStart;
    }, {});
  },
);

export const getChapterBreakFrames = createSelector(
  [getChapterBreaksByFrameStart],
  (chapterBreaks) => {
    if (isEmptyObject(chapterBreaks)) return [];
    return Object.keys(chapterBreaks).map(
      (key) => chapterBreaks[key].frameStart,
    );
  },
);

/**
 * @function shouldEnableChapterBreaks
 * todo(CONTENT-664): breaks should just be controlled by feature flag, not one or the other
 * (this includes publishers except Happening Now which can only have one Snap/Chapter)
 * @param {Object} state
 * @return bool true if chapter breaks should be enabled
 */
export const shouldEnableChapterBreaks = createSelector(
  [
    isUserAnAdvertiser,
    getIsPublisherHappeningNow,
    getIsChapterBreaksEnabled,
    isInEpisodeFlow,
  ],
  (isAdvertiser, isPublisherHappeningNow, breaksEnabled, isInEpisodeFlow) =>
    ((!isAdvertiser && !isPublisherHappeningNow) || breaksEnabled) && isInEpisodeFlow,
);

/**
 * @function shouldAutoInitializeChapterBreaks
 * todo(CONTENT-664): breaks should just be controlled by feature flag, not one or the other (this includes publishers)
 * @param {Object} state
 * @return bool true if chapter breaks should be auto initialized
 */
export const shouldAutoInitializeChapterBreaks = createSelector(
  [
    isUserAnAdvertiser,
    getIsPublisherHappeningNow,
    getIsChapterBreaksEnabled,
    getIsChapterBreaksShownByDefaultEnabled,
  ],
  (isAdvertiser, isPublisherHappeningNow, breaksEnabled, initializeEnabled) =>
    (!isAdvertiser && !isPublisherHappeningNow) ||
    (breaksEnabled && initializeEnabled),
);

/**
 * @function shouldManualInitializeBreaks
 * todo(CONTENT-664): breaks should just be controlled by feature flag, not one or the other (this includes publishers)
 * @param {Object} state
 * @return bool true if user would have to manually initialized chapter breaks
 */
export const shouldManualInitializeBreaks = createSelector(
  [
    isUserAnAdvertiser,
    getChapters,
    getIsChapterBreaksEnabled,
    getIsChapterBreaksShownByDefaultEnabled,
  ],
  (isAdvertiser, chapters, breaksEnabled, initializeEnabled) =>
    isAdvertiser &&
    breaksEnabled &&
    !initializeEnabled &&
    isEmptyArray(chapters),
);

/**
 * @function chapterBreakManualInitialized
 * @param {Object} state
 * @return bool true if user manually initialized chapter breaks
 */
export const chapterBreakManualInitialized = createSelector(
  [shouldAutoInitializeChapterBreaks, getChapters],
  (autoInitializeEnabled, chapters) => {
    return !autoInitializeEnabled && !isEmptyArray(chapters);
  },
);

/**
 * @function getEpisodeMaxChapterLengthInSeconds
 * @param {Object} state
 * @return {number}
 */
export const getEpisodeMaxChapterLengthInSeconds = createSelector(
  [getDiscoverPublisher],
  (discoverPublisher) => {
    // if user is not a publisher, use default
    if (isEmptyObject(discoverPublisher)) return CHAPTER_LENGTH_MAX_SECOND;

    const { isShowsPublisher, topSnapLimit } = discoverPublisher;

    // first set default max based on account
    const defaultRequiredMax = isShowsPublisher
      ? CHAPTER_LENGTH_MAX_SECOND_FOR_SHOW_PUBLISHER
      : CHAPTER_LENGTH_MAX_SECOND_FOR_PUBLISHER;

    // required to be at least minimum max chapter length (either, 10s or 20s)
    const discoverTopSnapLimit = isNumber(topSnapLimit) ? topSnapLimit : 0;

    return Math.max(discoverTopSnapLimit, defaultRequiredMax);
  },
);

/**
 * @function getEpisodeTargetChapterLengthInSeconds
 * @param {Object} state
 * @return {number}
 */
export const getEpisodeTargetChapterLengthInSeconds = createSelector(
  [
    getEpisodeMaxChapterLengthInSeconds,
    getTargetChapterLengthFromFeatureFlag,
    getDiscoverPublisher,
  ],
  (maxChapterLength, targetLengthFromFeatureFlag, discoverPublisher) => {
    if (
      !isEmptyObject(discoverPublisher) &&
      isNumber(discoverPublisher.targetSnapLengthMs)
    ) {
      const { targetSnapLengthMs } = discoverPublisher;
      return roundToDecimal(
        Math.max(
          CHAPTER_LENGTH_ABSOLUTE_MIN_SECOND,
          targetSnapLengthMs / SECOND_IN_MILLISECONDS,
        ),
      );
    }

    return roundToDecimal(
      Math.max(
        CHAPTER_LENGTH_ABSOLUTE_MIN_SECOND,
        Math.min(maxChapterLength, targetLengthFromFeatureFlag),
      ),
    );
  },
);

/**
 * @function getMaxFramesPerChapter
 * @param {Object} state
 * @return {number}
 */
export const getMaxFramesPerChapter = createSelector(
  [getEpisodeMaxChapterLengthInSeconds],
  (maxChapterLengthInSeconds) =>
    maxChapterLengthInSeconds * PLAYBACK_FRAMES_PER_SECOND,
);

/**
 * @function getHasInvalidChapterLength
 * @param {Object} state
 * @return {boolean} true if there's at least one chapter that has a duration that's too long, false otherwise
 */
export const getHasInvalidChapterLength = createSelector(
  [
    shouldAutoInitializeChapterBreaks,
    getChapters,
    getMaxFramesPerChapter,
    isInEpisodeFlow,
  ],
  (chaptersEnabled, chapters, maxFramesPerChapter, isInEpisodeFlow) => {
    if (!isInEpisodeFlow || (!chaptersEnabled && isEmptyArray(chapters))) {
      return false;
    }
    return chapters.some((chapter) => {
      return chapter.numUnskippedFrames > maxFramesPerChapter;
    });
  },
);

const activeShotContainerPassthrough = (state, activeShotContainerElement) => {
  return activeShotContainerElement;
};

export const getShotItemMetas = createSelector(
  [
    getAllShotContainersSortedByStartFrame,
    activeShotContainerPassthrough,
    getShotsByFrameRange,
    getAllElementsById,
    getAllContentById,
    getSortedSelectedShotRanges,
    getTotalTime,
    getChapterBreaksByFrameStart,
    getSourceVideoCreativeLayersByShotContainerElementId,
  ],
  (
    shotContainers,
    activeShotContainer,
    shotsByFrameRange,
    elementsById,
    contentById,
    sortedSelectedShotRanges,
    totalTime,
    chapterBreaksByFrameStart,
    sourceVideoCreativeLayersByShotContainerElementId,
  ) => {
    if (isEmptyArray(shotContainers)) return [];
    let currentChapterIndex = -1;
    const metas = shotContainers.map((shotContainerElement, index) => {
      const {
        contentId,
        elementId,
        frameStart,
        frameEnd,
        isTransformedByUser,
        meta = {},
      } = shotContainerElement;
      const { shotSkipped: isShotSkipped = false } = meta;
      const frameRange = new FrameRange(frameStart, frameEnd);

      const shot = shotsByFrameRange[frameRange.toString()];
      const hasShot = isNotNil(shot);

      const content = contentById[contentId];
      const { contentElementIds: viewElementIds } = content;
      const isChildTransformedByUser = viewElementIds.some((elementId) => {
        const viewElement = elementsById[elementId];
        return isNotNil(viewElement) && viewElement.isTransformedByUser;
      });
      const isAnalysisComplete = hasShot && shot.isAnalysisComplete;

      const loading =
        hasShot &&
        !(
          isTransformedByUser ||
          isChildTransformedByUser ||
          isAnalysisComplete
        );
      const isActive = shotContainerElement === activeShotContainer;
      let selected;
      if (isEmptyArray(sortedSelectedShotRanges)) {
        selected = isActive;
      } else {
        selected = sortedSelectedShotRanges.some(
          (selectedShotRange) =>
            FrameRange.isFrameRange(selectedShotRange) &&
            selectedShotRange.contains(frameRange),
        );
      }

      const sourceVideoCreativeLayerAtFrame =
        sourceVideoCreativeLayersByShotContainerElementId[elementId];
      const { frameIndex: adjustedFrameNumber } = isNotNil(
        sourceVideoCreativeLayerAtFrame,
      )
        ? sourceVideoCreativeLayerAtFrame.getRelativeFrameIndicesAndTimesAtFrame(
            frameStart,
          )
        : frameStart;

      const serverId = isNotNil(sourceVideoCreativeLayerAtFrame)
        ? sourceVideoCreativeLayerAtFrame.media.serverId
        : null;

      const thumbnailUrl = getEnvironmentSafeVRThumbnailUrl(
        adjustedFrameNumber,
        serverId,
        SHOT_LIST_THUMBNAIL_SIZE,
      );

      const frameMarker = Frame.getFrameMarker(frameStart);

      if (ChapterBreak.isChapterBreak(chapterBreaksByFrameStart[frameStart])) {
        currentChapterIndex++;
      }

      return new ShotGroupMeta(
        elementId,
        frameMarker,
        frameStart,
        frameEnd,
        index,
        isActive,
        loading,
        selected,
        thumbnailUrl,
        currentChapterIndex,
        isShotSkipped,
      );
    });

    return metas;
  },
);

/**
 * Get the media object that is related to the element with meta.shotType 'SOURCE'
 * @returns {object} the media object
 */
export const getSourceMedia = createSelector(
  [getAllContentById, getAllMediaById, getSourceShotElement],
  (allContentById, allMediaById, sourceShotElement) => {
    if (isNil(sourceShotElement)) return null;
    const sourceShotContent = allContentById[sourceShotElement.contentId];
    if (isNil(sourceShotContent)) return null;
    return allMediaById[sourceShotContent.mediaId];
  },
);

/**
 * Get the media id that is related to the element with meta.shotType 'SOURCE'
 * @returns {object} the media object
 */
export const getSourceMediaId = createSelector(
  [getSourceMedia],
  (sourceMedia) => {
    if (isNil(sourceMedia)) return null;
    return sourceMedia.mediaId;
  },
);

/**
 * Get the media objects of all elements with meta.shotType 'SOURCE'
 * @returns {array} array of media objects
 */
export const getAllSourceMedia = createSelector(
  [
    getAllContentById,
    getAllMediaById,
    getAllSourceShotElementsSortedByStartFrame,
  ],
  (allContentById, allMediaById, sourceShotElements) => {
    if (isEmptyArray(sourceShotElements)) return null;
    const sourceShotsContent = sourceShotElements.reduce(
      (acc, sourceShotElement) => {
        const sourceShotContent = allContentById[sourceShotElement.contentId];
        if (isNil(sourceShotContent)) return acc;
        acc.push(sourceShotContent);
        return acc;
      },
      [],
    );
    if (isEmptyArray(sourceShotsContent)) return null;
    return sourceShotsContent.reduce((acc, sourceShotContent) => {
      const sourceShotMedia = allMediaById[sourceShotContent.mediaId];
      if (isNil(sourceShotMedia)) return acc;
      acc.push(sourceShotMedia);
      return acc;
    }, []);
  },
);

/**
 * Get the media object that is related to the first source element, with respect to time
 * @returns {object} the media object
 */
export const getLastSourceMedia = createSelector(
  [
    getAllContentById,
    getAllMediaById,
    getAllSourceShotElementsSortedByStartFrame,
  ],
  (allContentById, allMediaById, sourceShotElements) => {
    if (isEmptyArray(sourceShotElements)) return null;
    const lastSourceShotElement =
      sourceShotElements[sourceShotElements.length - 1];
    const sourceShotContent = allContentById[lastSourceShotElement.contentId];
    if (isNil(sourceShotContent)) return null;
    return allMediaById[sourceShotContent.mediaId];
  },
);

/**
 * Get the end frame of the second last source media
 * @returns {number} end frame
 */
export const getEndFrameOfPreviousSourceElement = createSelector(
  [
    getAllContentById,
    getAllMediaById,
    getAllSourceShotElementsSortedByStartFrame,
  ],
  (allContentById, allMediaById, sourceShotElements) => {
    if (isEmptyArray(sourceShotElements) || sourceShotElements.length < 2) {
      return 0;
    }
    const secondLastSourceShotElement =
      sourceShotElements[sourceShotElements.length - 2];
    return secondLastSourceShotElement.frameEnd;
  },
);

/**
 * Get the number of frames to adjust the incoming shots by in the case of video clips
 * @returns {number} end frame
 */
export const getFramesToAdjustBy = createSelector(
  [getEndFrameOfPreviousSourceElement],
  (endFrameOfPreviousSourceElement) => {
    // If the value is zero, there is no previous source element
    if (endFrameOfPreviousSourceElement === 0) {
      return 0;
    }
    return endFrameOfPreviousSourceElement + 1;
  },
);

export const getSourceMediaForCurrentFrame = createSelector(
  [getAllContentById, getAllMediaById, getSourceShotElementForCurrentFrame],
  (allContentById, allMediaById, sourceShotElementForCurrentFrame) => {
    if (isNil(sourceShotElementForCurrentFrame)) return null;
    const sourceShotContent =
      allContentById[sourceShotElementForCurrentFrame.contentId];
    if (isNil(sourceShotContent)) return null;
    return allMediaById[sourceShotContent.mediaId];
  },
);

export const getAllMaskLayersByElementEffectGroupId = createSelector(
  [getAllLayersById],
  (allLayersById) => {
    const maskLayers = new Map();
    allLayersById.forEach((layer) => {
      if (Layer.isMaskLayer(layer)) {
        maskLayers.set(layer.groupId, layer);
      }
    });

    return maskLayers;
  },
);

/**
 * Get the bounds for a given shot view, by its element ID.
 * The bounds will either be based on the canvas or the mask layer.
 * @param {String} elementGroupId the ID of the view's parent element (contentType === ELEMENT_EFFECT_GROUP)
 * @returns {Rectangle} describing the bounds of the mask layer or the canvas
 */
export const getBoundsByShotViewElementId = createKeyedStateSelector(
  [
    getElementById,
    getAllMaskLayersByElementEffectGroupId,
    getCreativeRectangle,
  ],
  (shotViewElement, allMaskLayersByElementEffectGroupId, creativeRectangle) =>
    getBoundsByShotViewElementIdHelper(
      shotViewElement,
      allMaskLayersByElementEffectGroupId,
      creativeRectangle,
    ),
);

/**
 * Get the transform constraints for a given shot view, by its element ID.
 * They determine how the view can be transformed so that no whitespace is visible in the canvas.
 * @param {String} elementGroupId the ID of the view's parent element (contentType === ELEMENT_EFFECT_GROUP)
 * @returns {Constraints} derived from the element's sibling element (groupRole === MASK) or the canvas
 */
export const getConstraintsByShotViewElementId = createKeyedStateSelector(
  [getElementById, getLayerById, getSourceMedia, getBoundsByShotViewElementId],
  (shotViewElement, shotViewLayer, sourceMedia, bounds) =>
    getConstraintsByShotViewElementIdHelper(
      shotViewElement,
      shotViewLayer,
      sourceMedia,
      bounds,
    ),
);

export const canChangeShotLayouts = createSelector(
  [getSourceMediaForCurrentFrame],
  (sourceMedia) =>
    isNotNil(sourceMedia) &&
    sourceMedia.width / sourceMedia.height > MIN_SOURCE_MEDIA_RATIO_FOR_LAYOUTS,
);

/*
 * This selector helps calculating media segment edit view heights
 * only when creative's dimensions have been changed
 */
export const getAllMediaSegmentEditViewHeights = createSelector(
  [getCreativeRectangle],
  (creativeRectangle) => {
    return {
      [MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX]: getLetterBoxLayoutHeights(
        creativeRectangle,
      ),
      [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_2]: getSplitVertical2LayoutHeights(
        creativeRectangle,
      ),
      [MEDIA_SEGMENT_EDIT_TYPE.SPLIT_VERTICAL_3]: getSplitVertical3LayoutHeights(
        creativeRectangle,
      ),
      [MEDIA_SEGMENT_EDIT_TYPE.STATIC_CROP]: getStaticCropLayoutHeights(
        creativeRectangle,
      ),
    };
  },
);

const getMediaSegmentViewHeights = createSelector(
  [getActiveShotLayoutTypeForCurrentFrame, getAllMediaSegmentEditViewHeights],
  (activeShotLayout, mediaSegmentsEditViewHeights) => {
    return mediaSegmentsEditViewHeights[activeShotLayout];
  },
);

const getLayoutSizeByShotGroupElementIdHelper = (
  allLayersById,
  sourceShotElement,
  mediaSegmentEditViewHeights,
  activeMaskHeights,
  creativeWidth,
) => {
  // TODO determine set ratio from view heights
  if (isNil(mediaSegmentEditViewHeights)) return SHOT_LAYOUT_SIZES.CUSTOM;
  if (isEmptyArray(activeMaskHeights)) return SHOT_LAYOUT_SIZES.CUSTOM;

  let layoutSize = null;
  Object.entries(mediaSegmentEditViewHeights).forEach(
    ([layoutSizeKey, heights]) => {
      if (layoutSize) return;
      for (let i = 0; i < heights.length; i++) {
        const height = heights[i];
        // compare with scaled height of source media
        if (height === SHOT_VIEW_HEIGHT_FIT_WIDTH) {
          const sourceShotLayer = allLayersById.get(
            sourceShotElement.elementId,
          );
          const scaledMediaHeight =
            sourceShotLayer.height * (creativeWidth / sourceShotLayer.width);
          if (scaledMediaHeight !== activeMaskHeights[i]) return;
        } else if (height !== activeMaskHeights[i]) return;
      }
      layoutSize = layoutSizeKey;
    },
  );
  if (isNil(layoutSize)) return SHOT_LAYOUT_SIZES.CUSTOM;
  return layoutSize;
};

export const getLayoutSizeByShotGroupElementId = createSelector(
  [
    getAllLayersById,
    getSourceShotElement,
    getMediaSegmentViewHeights,
    getShotMaskHeightsByShotGroupElementId,
    getCreativeRectangle,
  ],
  (
    allLayersById,
    sourceShotElement,
    mediaSegmentEditViewHeights,
    activeMaskHeights,
    creativeRectangle,
  ) =>
    getLayoutSizeByShotGroupElementIdHelper(
      allLayersById,
      sourceShotElement,
      mediaSegmentEditViewHeights,
      activeMaskHeights,
      creativeRectangle.width,
    ),
);

export const getShotListItems = createSelector(
  [getChapterBreaksByFrameStart, getShotItemMetas],
  (chapterBreaks, shotMetas) => {
    if (isEmptyArray(shotMetas)) return [];

    const shotListItems = [];
    shotMetas.forEach((shotMeta) => {
      const { frameStart: shotFrameStart } = shotMeta;
      if (chapterBreaks[shotFrameStart]) {
        shotListItems.push(chapterBreaks[shotFrameStart]);
      }

      shotListItems.push(shotMeta);
    });

    return shotListItems;
  },
);

export const getVideoClipListItems = createSelector(
  [
    getAllSourceLayers,
    getSourceShotElementForCurrentFrame,
    getSortedSelectedShotRanges,
  ],
  (
    sourceLayers,
    sourceShotElementForCurrentFrame,
    sortedSelectedShotRanges,
  ) => {
    if (isEmptyArray(sourceLayers)) return null;
    const activeSourceShotElementId = isNotNil(sourceShotElementForCurrentFrame)
      ? sourceShotElementForCurrentFrame.elementId
      : null;
    return sourceLayers.map((sourceLayer, index) => {
      const {
        elementId,
        endFrame: frameEnd,
        mediumServerId: sourceVideoServerId,
        startFrame: frameStart,
        uploaded,
        content,
      } = sourceLayer;
      const frameRange = new FrameRange(frameStart, frameEnd);
      const firstFrameOfClip = Frame.secondsToFrame(content.startTime);
      const thumbnailUrl = uploaded
        ? getEnvironmentSafeVRThumbnailUrl(
            firstFrameOfClip,
            sourceVideoServerId,
            SHOT_LIST_THUMBNAIL_SIZE,
          )
        : null;
      const isActive = elementId === activeSourceShotElementId;
      const frameMarker = Frame.getFrameMarker(frameStart);
      const duration = Frame.frameToSeconds(frameRange.length);
      let selected;
      if (isEmptyArray(sortedSelectedShotRanges)) {
        selected = isActive;
      } else {
        selected = sortedSelectedShotRanges.some(
          (selectedShotRange) =>
            FrameRange.isFrameRange(selectedShotRange) &&
            selectedShotRange.contains(frameRange),
        );
      }
      // todo(kchatoo): create model VideoClipListItemMeta?
      return {
        duration,
        elementId,
        frameEnd,
        frameMarker,
        frameStart,
        index,
        isActive,
        selected,
        thumbnailUrl,
      };
    });
  },
);

export const getSelectedVideoClipListItem = createSelector(
  [getVideoClipListItems],
  (videoClipListItems) => {
    if (isEmptyArray(videoClipListItems)) return null;
    return videoClipListItems.find(({ selected }) => selected);
  },
);

/**
 * @function getAreSelectedShotRangesContiguous
 * @return {bool}
 */
export const getAreSelectedShotRangesContiguous = createSelector(
  [getSortedSelectedShotRanges],
  (selectedShotRanges) => selectedShotRanges.length === 1,
);

export const canMergeSelectedShots = createSelector(
  [
    getAreSelectedShotRangesContiguous,
    getSortedSelectedShotRanges,
    getAllShotContainersSortedByStartFrame,
    getSourceVideoCreativeLayersByShotContainerElementId,
    getSelectedShotContainerLayers,
  ],
  (
    areSelectedShotRangesContiguous,
    sortedSelectedShotRanges,
    shotContainers,
    sourceVideoCreativeLayersByShotContainerElementId,
    shotsInSelection,
  ) => {
    if (!areSelectedShotRangesContiguous) return false;
    const hasShotsInSelection = isArray(shotsInSelection);

    let sourceLayer = null;
    const isSelectedOverMultipleClips =
      hasShotsInSelection &&
      shotsInSelection.some((shot) => {
        if (isNotNil(shot)) {
          const currentSourceLayer =
            sourceVideoCreativeLayersByShotContainerElementId[shot.elementId];
          if (sourceLayer === null) {
            sourceLayer = currentSourceLayer;
          }
          return sourceLayer !== currentSourceLayer;
        }
        return false;
      });

    return (
      hasShotsInSelection &&
      shotsInSelection.length > 1 &&
      !isSelectedOverMultipleClips
    );
  },
);

export const canDirectlyManipulateCustomSplit = createSelector(
  [getNumberOfSelectedShots, getActiveShotLayoutTypeForCurrentFrame],
  (numberOfSelectedShots, activeShotLayoutType) => {
    return (
      numberOfSelectedShots <= 1 &&
      MEDIA_SEGMENT_EDIT_LAYOUT_SUPPORTS_DIRECT_MANIUPULATION[
        activeShotLayoutType
      ]
    );
  },
);

export const getEpisodeMinChapterLengthInSeconds = createSelector(
  [
    getEpisodeTargetChapterLengthInSeconds,
    getEpisodeMinChapterLengthInSecondsFromFeatureFlag,
  ],
  (targetChapter, featureFlagMin) => {
    const episodeMinChapter = isNotNumber(featureFlagMin)
      ? CHAPTER_LENGTH_MIN_SECOND
      : featureFlagMin;
    return Math.min(targetChapter, episodeMinChapter);
  },
);

const areChaptersLongerThanMinThreshold = (
  previousChapterFrameRange,
  nextChapterFrameRange,
  shots,
  episodeMinSnapLengthInSeconds,
) => {
  const shotsInPreviousChapterFrameRange = getShotsForFrameRange(
    previousChapterFrameRange,
    shots,
  );
  const shotsInNextChapterFrameRange = getShotsForFrameRange(
    nextChapterFrameRange,
    shots,
  );
  const tentativePreviousChapter = new Chapter(
    null,
    previousChapterFrameRange.start,
    previousChapterFrameRange.end,
    shotsInPreviousChapterFrameRange,
  );
  const tentativeNextChapter = new Chapter(
    null,
    nextChapterFrameRange.start,
    nextChapterFrameRange.end,
    shotsInNextChapterFrameRange,
  );
  return (
    tentativePreviousChapter.duration >= episodeMinSnapLengthInSeconds &&
    tentativeNextChapter.duration >= episodeMinSnapLengthInSeconds
  );
};

export const canAddChapterBreakAboveSelectedShots = createSelector(
  [
    getFirstSelectedShotRanges,
    getChapters,
    getEpisodeMinChapterLengthInSeconds,
    getAllShotContainerLayers,
  ],
  (selectedFrameRange, chapters, episodeMinSnapLengthInSeconds, shots) => {
    if (
      isNil(selectedFrameRange) ||
      !FrameRange.isFrameRange(selectedFrameRange)
    ) {
      return false;
    }

    if (isEmptyArray(chapters)) return false;

    // if selected range is empty, use active shot as where chapter break will be added
    const { start: chapterBreakFrame } = selectedFrameRange;
    const chapter = chapters.find((chapter) =>
      chapter.frameRange.containsFrameNumber(chapterBreakFrame),
    );

    // should this be true if shots aren't grouped in chapter
    if (!Chapter.isChapter(chapter)) return false;

    const previousChapterFrameRange = new FrameRange(
      chapter.frameStart,
      chapterBreakFrame - 1,
    );
    const nextChapterFrameRange = new FrameRange(
      chapterBreakFrame,
      chapter.frameEnd,
    );
    return areChaptersLongerThanMinThreshold(
      previousChapterFrameRange,
      nextChapterFrameRange,
      shots,
      episodeMinSnapLengthInSeconds,
    );
  },
);

export const canAddChapterBreakBelowSelectedShots = createSelector(
  [
    getFirstSelectedShotRanges,
    getChapters,
    getEpisodeMinChapterLengthInSeconds,
    getAllShotContainerLayers,
  ],
  (selectedFrameRange, chapters, episodeMinSnapLengthInSeconds, shots) => {
    if (
      isNil(selectedFrameRange) ||
      !FrameRange.isFrameRange(selectedFrameRange)
    ) {
      return false;
    }

    if (isEmptyArray(chapters)) return false;

    // if selected range is empty, use active shot as where chapter break will be added
    const { end: chapterBreakFrame } = selectedFrameRange;
    const chapter = chapters.find((chapter) =>
      chapter.frameRange.containsFrameNumber(chapterBreakFrame),
    );

    if (!Chapter.isChapter(chapter)) return false;

    const previousChapterFrameRange = new FrameRange(
      chapter.frameStart,
      chapterBreakFrame,
    );
    const nextChapterFrameRange = new FrameRange(
      chapterBreakFrame + 1,
      chapter.frameEnd,
    );
    return areChaptersLongerThanMinThreshold(
      previousChapterFrameRange,
      nextChapterFrameRange,
      shots,
      episodeMinSnapLengthInSeconds,
    );
  },
);

export const canToggleSkipForSelectedShots = createSelector(
  [getSelectedOrActiveFrameRange],
  (selectedFrameRange) => {
    if (!FrameRange.isFrameRange(selectedFrameRange)) return false;
    return true;
  },
);

export const isEpisodeCreativeReady = createSelector(
  [
    getCurrentCreativeId,
    shouldAutoInitializeChapterBreaks,
    getChapters,
    areAllShotsDetectedOrCreated,
  ],
  (creativeId, chaptersEnabled, chapters, allShotsDetectedOrCreated) => {
    // REQUIRED - creativeId
    if (isNil(creativeId)) return false;

    // REQUIRED - chapters
    if (chaptersEnabled && isEmptyArray(chapters)) return false;

    // REQUIRED - either all shots have to been created or shot detection is set to true from the server
    return allShotsDetectedOrCreated;
  },
);

export const getCanShowReviewModal = createSelector(
  [getIsShowingReviewModal, isInEpisodeFlow, isEpisodeCreativeReady],
  (isShowingReviewModal, inEpisodeFlow, episodeCreativeReady) => {
    return !isShowingReviewModal && inEpisodeFlow && episodeCreativeReady;
  },
);

export const getEpisodeLogTags = createSelector(
  [getSourceMedia, getCreativeId],
  (sourceMedia, creativeId) => {
    const tags = {
      source_media: isNotNil(sourceMedia) ? sourceMedia.serverId : null,
    };
    if (isNotNil(creativeId)) {
      tags.creative_id = creativeId;
    }

    return tags;
  },
);

/**
 * @function getLayoutPropertiesOfSelectedShots
 * @param {Object} state
 * @return {Object} consisting of backgroundColor, colorsAreMixed, showCustomLayoutRatio
 */
export const getLayoutPropertiesOfSelectedShots = createSelector(
  [
    getSelectedShotContainerLayers,
    getAllLayersById,
    getSourceShotElement,
    getMediaSegmentViewHeights,
    getAllContentById,
    getAllElementsById,
    getCreativeRectangle,
  ],
  (
    shotContainers,
    allLayersById,
    sourceShotElement,
    mediaSegmentEditViewHeights,
    contentById,
    elementMap,
    creativeRectangle,
  ) => {
    const selectedShotBackgrounds = new ShotBackgroundCollection();
    let showCustomLayoutRatio = false;
    shotContainers.forEach((shotGroupLayer) => {
      const { elementId: shotGroupElementId } = shotGroupLayer;
      const elementDescendants = getElementDescendantsHelper(
        contentById,
        elementMap,
        shotGroupElementId,
      );
      const shotMaskLayers = getAllShotMaskLayersByShotGroupElementIdHelper(
        allLayersById,
        elementDescendants,
      );
      const activeMaskHeights = getShotMaskHeightsByShotGroupElementIdHelper(
        allLayersById,
        shotMaskLayers,
      );
      const activeShotLayoutSize = getLayoutSizeByShotGroupElementIdHelper(
        allLayersById,
        sourceShotElement,
        mediaSegmentEditViewHeights,
        activeMaskHeights,
        creativeRectangle.width,
      );
      const backgroundLayer = getBackgroundLayerByShotGroupElementIdHelper(
        allLayersById,
        elementDescendants,
      );
      const useCustomLayoutSizeForThisShot =
        activeShotLayoutSize === SHOT_LAYOUT_SIZES.CUSTOM;
      showCustomLayoutRatio =
        showCustomLayoutRatio || useCustomLayoutSizeForThisShot;

      // populate selected backgrounds
      if (isNotNil(backgroundLayer)) {
        const backgroundType = ShotBackground.getShotBackgroundType(
          backgroundLayer.contentType,
        );
        const shotBackground = new ShotBackground(backgroundType);

        // set value if it is a fill background type
        if (backgroundType === SHOT_BACKGROUND_TYPES.FILL) {
          shotBackground.value = backgroundLayer.fillColor;
        }

        selectedShotBackgrounds.addShotBackground(shotBackground);
      }
    });

    const colorsAreMixed = ShotBackgroundCollection.hasMixedColorValues(
      selectedShotBackgrounds,
    );

    return {
      colorsAreMixed,
      showCustomLayoutRatio,
      selectedShotBackgrounds,
    };
  },
);

/**
 * Gets an array of arrays of layers of selected shots. Selected shots are assumed to have the same layout.
 * Each each inner array spans all selected shots, and there are N + 1 spanning arrays, where N is
 * the number of splits in the selected layout. E.g., if 4 split shots are selected, then this selector
 * will return an array A such that A.length === 2 and A[0].length === 4.
 * @function getLayerArraysAcrossAllSelectedShots
 * @param {Object} state
 * @return {array} array of arrays of layers
 */
export const getLayerArraysAcrossAllSelectedShots = createSelector(
  [
    getSelectedShotContainerLayers,
    getAllLayersById,
    getAllContentById,
    getAllElementsById,
  ],
  (selectedShotContainerLayers, allLayersById, contentById, elementMap) => {
    if (isEmptyArray(selectedShotContainerLayers)) return [];

    let layerArraysAcrossAllSelectedShots = null;
    for (let i = 0; i < selectedShotContainerLayers.length; i += 1) {
      const shot = selectedShotContainerLayers[i];
      const { elementId: shotGroupElementId } = shot;

      const elementDescendants = getElementDescendantsHelper(
        contentById,
        elementMap,
        shotGroupElementId,
      );
      const layersOfCurrentShot = getAllLayersOfTypeByShotGroupElementIdHelper(
        allLayersById,
        elementDescendants,
        SHOT_ELEMENT_TYPES.VIEW,
      );

      // Initialize layerArraysAcrossAllSelectedShots. NB, we assume that if transformation controls are shown,
      // all layouts in selection are the same and the number of layers per shot are the same
      if (isNil(layerArraysAcrossAllSelectedShots)) {
        layerArraysAcrossAllSelectedShots = initializeArray(
          layersOfCurrentShot.length,
          () => [],
        );
      }
      for (let j = 0; j < layersOfCurrentShot.length; j += 1) {
        const layer = layersOfCurrentShot[j];
        const layerArrayAcrossAllSelectedShots =
          layerArraysAcrossAllSelectedShots[j];
        if (isArray(layerArrayAcrossAllSelectedShots)) {
          layerArrayAcrossAllSelectedShots.push(layer);
        }
      }
    }
    return layerArraysAcrossAllSelectedShots;
  },
);

/**
 * @function getTransformInfoForLayersAcrossAllSelectedShots
 * @param {Object} state
 * @param {array} layerArrayAcrossAllSelectedShots
 * @return {Object}
 */
export const getTransformInfoForLayersAcrossAllSelectedShots = createSelector(
  [
    getAllElementsById,
    getAllMaskLayersByElementEffectGroupId,
    getAllContentById,
    getAllMediaById,
    getSourceMediaForCurrentFrame,
    getLayerArraysAcrossAllSelectedShots,
    getCreativeRectangle,
  ],
  (
    elementMap,
    allMaskLayersByElementEffectGroupId,
    contentById,
    mediaById,
    sourceMedia,
    layerArraysAcrossAllSelectedShots,
    creativeRectangle,
  ) => {
    return generateTransformInfoFromLayers(
      layerArraysAcrossAllSelectedShots,
      elementMap,
      allMaskLayersByElementEffectGroupId,
      contentById,
      mediaById,
      sourceMedia,
      creativeRectangle,
    );
  },
);

export const getStartAndEndTransformFrameNumberByElementId = createKeyedStateSelector(
  [getElementById],
  (shotViewElement) => {
    if (isNil(shotViewElement)) return null;
    const { transforms } = shotViewElement;
    const transformFrames = isObject(transforms)
      ? Object.keys(transforms)
      : null;
    // if there are < 2 transforms for the element, the element will have no animation and thus
    // no start or end transform
    if (transformFrames.length < 2) return null;
    const [startFrame, endFrame] = transformFrames;
    return {
      startFrame,
      endFrame,
    };
  },
);

export const getTransformKeyFramesOfSelectedElement = createSelector(
  [getAllElementsById, getSelectedDisplayElementId],
  (elementMap, selectedElementId) => {
    if (isNil(selectedElementId)) return null;
    const element = elementMap[selectedElementId];
    if (isNil(element)) return null;

    const { transforms } = element;
    return Object.keys(transforms).map((transform) => parseInt(transform, 10));
  },
);

export const getIsCurrentFrameOutOfSelectedFrameRange = createSelector(
  [getAllElementsById, getCurrentFrame, getSelectedDisplayElementId],
  (elementMap, currentFrame, selectedElementId) => {
    if (isNil(selectedElementId)) return true;
    const element = elementMap[selectedElementId];
    if (isNil(element)) return true;

    const { frameEnd, frameStart } = element;
    return currentFrame > frameEnd || currentFrame < frameStart;
  },
);

export const canShowPanKeyFrames = createSelector(
  [
    getTransformKeyFramesOfSelectedElement,
    getIsCurrentFrameOutOfSelectedFrameRange,
  ],
  (
    transformKeyFramesOfSelectedElement,
    isCurrentFrameOutOfSelectedFrameRange,
  ) => {
    if (isNil(transformKeyFramesOfSelectedElement)) return false;
    return (
      !isCurrentFrameOutOfSelectedFrameRange &&
      transformKeyFramesOfSelectedElement.length >= 2
    ); /* two or more transformations on selected shot means it is in 'pan' mode */
  },
);

const shotGroupElementIdPassThrough = (state, shotGroupElementId) =>
  shotGroupElementId;

export const getLayersByShotGroupElementId = createKeyedStateSelector(
  [
    getAllLayersById,
    getAllContentById,
    getAllElementsById,
    shotGroupElementIdPassThrough,
  ],
  (allLayersById, contentById, elementMap, shotGroupElementId) => {
    if (isNil(shotGroupElementId)) return [];

    let layersArray = null;

    const elementDescendants = getElementDescendantsHelper(
      contentById,
      elementMap,
      shotGroupElementId,
    );
    const layersOfCurrentShot = getAllLayersOfTypeByShotGroupElementIdHelper(
      allLayersById,
      elementDescendants,
      SHOT_ELEMENT_TYPES.VIEW,
    );

    layersArray = initializeArray(layersOfCurrentShot.length, () => []);

    for (let j = 0; j < layersOfCurrentShot.length; j++) {
      const layer = layersOfCurrentShot[j];
      const layerArrayAcrossAllShots = layersArray[j];
      layerArrayAcrossAllShots.push(layer);
    }

    return layersArray;
  },
);

export const getTransformInfoForLayersByShotGroupElementId = createKeyedStateSelector(
  [
    getAllElementsById,
    getAllMaskLayersByElementEffectGroupId,
    getAllContentById,
    getAllMediaById,
    getSourceMediaForCurrentFrame,
    getLayersByShotGroupElementId,
    getCreativeRectangle,
  ],
  (
    elementMap,
    allMaskLayersByElementEffectGroupId,
    contentById,
    mediaById,
    sourceMedia,
    layersArrayOfShotGroupElementId,
    creativeRectangle,
  ) => {
    return generateTransformInfoFromLayers(
      layersArrayOfShotGroupElementId,
      elementMap,
      allMaskLayersByElementEffectGroupId,
      contentById,
      mediaById,
      sourceMedia,
      creativeRectangle,
    );
  },
);

/**
 * @function getLayoutInfoOfSelectedShots
 * @param {Object} state
 * @return {Object} consisting of allSelectedShotsMaskHeights and layoutSizesOfSelectedShots
 */
export const getLayoutInfoOfSelectedShots = createSelector(
  [
    getMediaSegmentViewHeights,
    getAllContentById,
    getAllLayersById,
    getAllElementsById,
    getSelectedShotContainerLayers,
    getSourceShotElement,
    getCreativeRectangle,
  ],
  (
    mediaSegmentEditViewHeights,
    contentById,
    allLayersById,
    elementMap,
    selectedShotContainerLayers,
    sourceShotElement,
    creativeRectangle,
  ) => {
    const layoutSizesOfSelectedShots = new Set();
    const allSelectedShotsMaskHeights = [];
    selectedShotContainerLayers.forEach((shotContainerLayer) => {
      const { elementId: shotGroupElementId } = shotContainerLayer;
      const elementDescendants = getElementDescendantsHelper(
        contentById,
        elementMap,
        shotGroupElementId,
      );
      const shotMaskLayers = getAllShotMaskLayersByShotGroupElementIdHelper(
        allLayersById,
        elementDescendants,
      );
      const activeMaskHeights = getShotMaskHeightsByShotGroupElementIdHelper(
        allLayersById,
        shotMaskLayers,
      );
      const activeShotLayoutSize = getLayoutSizeByShotGroupElementIdHelper(
        allLayersById,
        sourceShotElement,
        mediaSegmentEditViewHeights,
        activeMaskHeights,
        creativeRectangle.width,
      );
      allSelectedShotsMaskHeights.push(activeMaskHeights);
      layoutSizesOfSelectedShots.add(activeShotLayoutSize);
    });
    return {
      allSelectedShotsMaskHeights,
      layoutSizesOfSelectedShots,
    };
  },
);

export const getMaskHeightsOfShotGroupElementId = createKeyedStateSelector(
  [
    getAllContentById,
    getAllLayersById,
    getAllElementsById,
    shotGroupElementIdPassThrough,
  ],
  (contentById, allLayersById, elementMap, shotGroupElementId) => {
    const elementDescendants = getElementDescendantsHelper(
      contentById,
      elementMap,
      shotGroupElementId,
    );
    const shotMaskLayers = getAllShotMaskLayersByShotGroupElementIdHelper(
      allLayersById,
      elementDescendants,
    );
    const activeMaskHeights = getShotMaskHeightsByShotGroupElementIdHelper(
      allLayersById,
      shotMaskLayers,
    );
    return activeMaskHeights;
  },
);

/**
 * @function getShotBackgroundByShotGroupElementId
 * @param {Object} state
 * @param {string} elementId of shot group
 * @return {ShotBackground} shotBackground
 */
export const getShotBackgroundByShotGroupElementId = createKeyedStateSelector(
  [
    getAllLayersById,
    getAllContentById,
    getAllElementsById,
    shotGroupElementIdPassThrough,
  ],
  (allLayersById, contentById, elementMap, shotGroupElementId) => {
    const elementDescendants = getElementDescendantsHelper(
      contentById,
      elementMap,
      shotGroupElementId,
    );
    const backgroundLayer = getBackgroundLayerByShotGroupElementIdHelper(
      allLayersById,
      elementDescendants,
    );

    if (isNil(backgroundLayer)) return null;

    const backgroundType = ShotBackground.getShotBackgroundType(
      backgroundLayer.contentType,
    );
    const shotBackground = new ShotBackground(backgroundType);

    // set value if it is a fill background type
    if (backgroundType === SHOT_BACKGROUND_TYPES.FILL) {
      shotBackground.value = backgroundLayer.fillColor;
    }

    return shotBackground;
  },
);

export const canToggleFocusMode = createSelector(
  [getRootEndFrame],
  (rootEndFrame) => rootEndFrame > EPISODE_TIMELINE_ZOOMED_IN_WIDTH_IN_FRAMES,
);

/**
 * @function getFirstPlayableFrameNumber
 * @param {Layer[]} Non skipped shot container layers
 * @return {Number} First valid frame of video
 */
export const getFirstPlayableFrameNumber = createSelector(
  [getNonSkippedShotContainerLayers],
  (nonSkippedShotContainerLayers) => {
    if (
      isNil(nonSkippedShotContainerLayers) ||
      isEmptyArray(nonSkippedShotContainerLayers)
    ) {
      return null;
    }
    // We can be confident that [0] is our first shot as we inherently use getAllShotContainersSortedByStartFrame
    return nonSkippedShotContainerLayers[0].startFrame;
  },
);

/**
 * @function getIsLetterboxMajority
 * @param {Object[]} Elements that correspond to a shotGroup
 * @return {Boolean} If the majority of shot groups are of type letterbox
 */
export const getIsLetterboxMajority = createSelector(
  [getAllShotContainers, isInEpisodeFlow],
  (shotContainers, isInEpisodeFlow) => {
    if (
      !isInEpisodeFlow ||
      !isArray(shotContainers) ||
      isEmptyArray(shotContainers)
    ) {
      return false;
    }
    const numOfShots = shotContainers.length;
    const numOfLetterboxShots = shotContainers.reduce((acc, shotContainer) => {
      return shotContainer.meta.layoutType === MEDIA_SEGMENT_EDIT_TYPE.LETTERBOX
        ? acc + 1
        : acc;
    }, 0);
    return numOfLetterboxShots / numOfShots >= MAXIMUM_LETTERBOX_THRESHOLD;
  },
);

export const getActivePanKeyFrameHelper = (
  currentFrame,
  transformKeyFramesOfSelectedElement,
) => {
  let activePanKeyFrame = null;
  if (
    isArray(transformKeyFramesOfSelectedElement) &&
    transformKeyFramesOfSelectedElement.length >= 2
  ) {
    const panStartFrame = transformKeyFramesOfSelectedElement[0];
    const panEndFrame = transformKeyFramesOfSelectedElement[1];
    if (currentFrame <= panStartFrame) {
      activePanKeyFrame = 0;
    } else if (currentFrame >= panEndFrame) {
      activePanKeyFrame = 1;
    }
  }
  return activePanKeyFrame;
};

export const getActivePanKeyFrame = createSelector(
  [getCurrentFrame, getTransformKeyFramesOfSelectedElement],
  (currentFrame, transformKeyFramesOfSelectedElement) =>
    getActivePanKeyFrameHelper(
      currentFrame,
      transformKeyFramesOfSelectedElement,
    ),
);

export const isShotListTabSelected = createSelector(
  [getActiveLeftPanelTab],
  (activeLeftPanelTab) => activeLeftPanelTab === LEFT_PANEL_TABS.SHOTS,
);

export const isClipListTabSelected = createSelector(
  [getActiveLeftPanelTab],
  (activeLeftPanelTab) => activeLeftPanelTab === LEFT_PANEL_TABS.CLIPS,
);

/**
 * @function getEpisodeDurationLeftInSeconds
 * calculate the amount of duration left to add clips
 * @return {number}
 */
export const getEpisodeDurationLeftInSeconds = createSelector(
  [getTotalTime, getMaxEpisodeDurationInSeconds],
  (totalTime, maxEpisodeDurationInSeconds) =>
    maxEpisodeDurationInSeconds -
    EPISODE_MAX_LENGTH_WIGGLE_ROOM_IN_SECONDS -
    totalTime,
);

/**
 * @function canCreateClipFromShots
 * Checks if the user can create a clip from selected shots
 * shot range must be contiguous AND is within the same clip to be valid for clip creation
 * @return {bool}
 */
export const canCreateClipFromShots = createSelector(
  [
    getAreSelectedShotRangesContiguous,
    getCurrentSourceLayerFrameRange,
    getSortedSelectedShotRanges,
    getEpisodeDurationLeftInSeconds,
    isEpisodeCreativeReady,
  ],
  (
    areSelectedShotRangesContiguous,
    currentSourceLayerFrameRange,
    selectedShotRanges,
    episodeDurationLeft,
    episodeCreativeReady,
  ) => {
    if (!episodeCreativeReady) return false;
    if (!FrameRange.isFrameRange(currentSourceLayerFrameRange)) return false;
    if (!areSelectedShotRangesContiguous) return false;

    const selectedShotRange = selectedShotRanges[0];
    const isMaxDuration =
      Frame.frameToSeconds(selectedShotRange.length) > episodeDurationLeft;

    return (
      currentSourceLayerFrameRange.contains(selectedShotRanges[0]) &&
      !isMaxDuration
    );
  },
);
